import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import {
  ActionCard,
  Field,
  KeyValue,
  MtlsPanel,
  SiteLogViewer,
  PageHeader,
  StatusPill,
  VhostOptionsForm,
  usePluginAvailability,
  type KVRow,
  type VhostOptions,
} from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// One site, end to end: what it serves, its certificate, whether DNS actually points here, who
// may connect with a client certificate, and what has been hitting it.
//
// These five answers were each reachable only by curl. They belong together because the question
// an operator arrives with — "why is this site not working" — is answered by whichever of them is
// wrong, and the wrong one is never predictable.

interface Vhost {
  id: number;
  domain: string;
  upstream: string;
  tls_mode: string;
  tls_pending?: boolean;
  mode?: string;
  root?: string;
  tenant_id?: number | null;
  opts?: VhostOptions;
  app?: { name?: string } | null;
}
interface Cert {
  kind?: string;
  issuer?: string;
  not_before?: string;
  not_after?: string;
  days_left?: number;
  expired?: boolean;
  self_signed?: boolean;
  /** Publicly trusted: the chain reaches a public root (certfacts.Public). False for a self-signed
   *  leaf AND for Caddy's internal authority, which is not self-signed. */
  public?: boolean;
  /** The certificate names this site. */
  covers?: boolean;
  sans?: string[] | null;
}
interface Dns {
  fqdn?: string;
  resolvable?: boolean;
  has_record?: boolean;
  resolved_ips?: string[] | null;
  node_ips?: string[] | null;
  record_types?: string[] | null;
  pdns_checked?: boolean;
  canonical_redirect?: unknown;
}

// BackendEditor changes where a proxy site sends traffic. The policy is enforced server-side: a
// public address, a private/LAN address, or an app on this server (127.0.0.1 on its port) is
// accepted; a platform-protected address is refused with a 409 the admin can knowingly confirm.
function BackendEditor({ domain, current }: { domain: string; current: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [value, setValue] = React.useState(current);
  const [busy, setBusy] = React.useState(false);
  const [internalReason, setInternalReason] = React.useState<string | null>(null);
  React.useEffect(() => {
    setValue(current);
  }, [current]);

  const save = async (confirmInternal: boolean) => {
    const up = value.trim();
    if (!up) return;
    setBusy(true);
    try {
      await client.apiFetch(`/caddy/vhosts/${encodeURIComponent(domain)}`, {
        method: "PUT",
        body: JSON.stringify(confirmInternal ? { upstream: up, confirm_internal: true } : { upstream: up }),
      });
      notify({ kind: "success", title: "Backend updated", body: `${domain} now sends traffic to ${up}` });
      setInternalReason(null);
      void qc.invalidateQueries({ queryKey: ["vhost", domain] });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const marker = "confirm_internal_required:";
      const i = msg.indexOf(marker);
      if (i >= 0) {
        setInternalReason(msg.slice(i + marker.length).trim());
      } else {
        notify({ kind: "error", title: "Could not update the backend", body: msg });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Field mono label="Where this site sends traffic" value={value} onChange={setValue} placeholder="203.0.113.9:8080" />
      <p className="text-[11.5px] leading-relaxed text-muted">
        A public address, the tenant’s own private/LAN address, or one of their apps on this server
        (<span className="font-mono">127.0.0.1</span> on its port). The platform’s own internal
        addresses are refused — as a super-admin you can confirm one deliberately.
      </p>
      <Button
        size="sm"
        variant="soft"
        disabled={busy || !value.trim() || value.trim() === current}
        onClick={() => void save(false)}
      >
        {busy ? "Saving…" : "Save backend"}
      </Button>
      <ConfirmDialog
        open={internalReason !== null}
        onOpenChange={(o) => {
          if (!o) setInternalReason(null);
        }}
        title="This points at an internal address"
        confirmLabel="I understand, proceed"
        body={
          <span>
            That destination is {internalReason}. Proceeding lets this public site reach a
            platform-internal address — continue only if you intend to.
          </span>
        }
        onConfirm={() => void save(true)}
      />
    </div>
  );
}

export function VhostDetailScreen() {
  const { domain = "" } = useParams();
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();

  const vhost = useQuery({
    queryKey: ["vhost", domain],
    queryFn: () => client.apiFetch<Vhost>(`/caddy/vhosts/${encodeURIComponent(domain)}`),
  });
  // Which plugin-backed directives the RUNNING binary actually has. Without this the form would
  // offer WAF, rate limiting, cache, brotli and geo as ordinary switches on a build that cannot
  // honour them — a setting that reports itself on and does nothing, which is the interlock rule.
  const pluginAvail = usePluginAvailability();

  // The record MERGES (upstream, mode, root and tls_mode are left alone when not supplied), but
  // `opts` REPLACES THE WHOLE OPTION SET — vhostBody documents it as "the per-vhost advanced
  // editor sends this to replace all opts".
  //
  // So the form must round-trip every field it was given, including the ones it does not render:
  // auth_areas, rewrites, app_routes, the mtls settings. Dropping one would silently switch off a
  // password-protected directory or client-certificate enforcement, with a success toast. It works
  // because the form spreads the server's object and only overwrites individual keys; that
  // property is load-bearing, not incidental, and is asserted by a test in the kit package.
  const saveOpts = useMutation({
    mutationFn: (o: VhostOptions) =>
      client.apiFetch<unknown>(`/caddy/vhosts/${encodeURIComponent(domain)}`, {
        method: "PUT",
        body: JSON.stringify({ opts: o }),
      }),
    onSuccess: () => {
      notify({ kind: "success", title: "Site settings saved" });
      void qc.invalidateQueries({ queryKey: ["vhost", domain] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not save", body: e instanceof Error ? e.message : String(e) }),
  });

  const cert = useQuery({
    queryKey: ["vhost-cert", domain],
    queryFn: () => client.apiFetch<Cert>(`/caddy/vhosts/${encodeURIComponent(domain)}/cert`),
    retry: false,
  });
  const dns = useQuery({
    queryKey: ["vhost-dns", domain],
    queryFn: () => client.apiFetch<Dns>(`/caddy/vhosts/${encodeURIComponent(domain)}/dns`),
    retry: false,
  });

  const v = vhost.data;
  const c = cert.data;
  const d = dns.data;

  // The certificate's grade is the interlock: "issued" means nothing if it is self-signed, and
  // "valid" means nothing if it expires on Thursday.
  // Graded on `public`, not `self_signed`: Caddy's internal certificate is signed by Caddy's own
  // intermediate, so it is NOT self-signed and read as fine here while every browser warned.
  const notTrusted = c?.public === false || c?.self_signed === true;
  const certTone = c?.expired || c?.covers === false ? "bad" : notTrusted ? "warn" : (c?.days_left ?? 99) < 14 ? "warn" : "ok";
  const certWord = c?.expired
    ? "expired"
    : c?.covers === false
      ? "does not name this site"
      : notTrusted
        ? c?.self_signed
          ? "self-signed"
          : "not publicly trusted"
      : c?.days_left != null
        ? `${c.days_left} days left`
        : "unknown";

  // DNS pointing HERE is the thing, not DNS existing. A record that resolves somewhere else is
  // the most common reason a site 404s or serves the wrong content on a cluster.
  const pointsHere =
    (d?.resolved_ips ?? []).length > 0 &&
    (d?.node_ips ?? []).some((ip) => (d?.resolved_ips ?? []).includes(ip));

  const certRows: KVRow[] = [
    { key: "kind", label: "Kind", value: c?.kind },
    { key: "issuer", label: "Issued by", value: c?.issuer },
    {
      key: "public",
      label: "Publicly trusted",
      value: c?.public,
      goodWhen: true,
      hint: "The chain reaches a public root, so browsers accept it. Caddy's internal certificate and a private authority's are not — every visitor gets a full-page warning.",
    },
    {
      key: "covers",
      label: "Names this site",
      value: c?.covers,
      goodWhen: true,
    },
    {
      key: "self_signed",
      label: "Self-signed",
      value: c?.self_signed,
      goodWhen: false,
      hint: "A self-signed certificate makes every browser show a full-page warning before any content. It usually means the name does not resolve here yet, so ACME was deliberately held off.",
    },
    { key: "not_after", label: "Expires", value: c?.not_after },
    { key: "sans", label: "Covers", value: c?.sans ?? [] },
  ];


  return (
    <div>
      <PageHeader
        title={domain}
        subtitle="What this site serves, and whether the internet can reach it"
        actions={
          <div className="flex flex-wrap gap-2">
            <StatusPill tone={certTone}>{certWord}</StatusPill>
            <StatusPill tone={pointsHere ? "ok" : "warn"}>{pointsHere ? "DNS points here" : "DNS elsewhere"}</StatusPill>
          </div>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tls">Certificate &amp; DNS</TabsTrigger>
          <TabsTrigger value="configure">Configure</TabsTrigger>
          <TabsTrigger value="mtls">Client certificates</TabsTrigger>
          <TabsTrigger value="log">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Section title="Site" bodyClassName="p-0">
            <StateView loading={vhost.isLoading} error={vhost.error} errorContext="this site">
              <KeyValue
                rows={[
                  { key: "upstream", label: "Serves from", value: v?.upstream || v?.root },
                  { key: "mode", label: "Mode", value: v?.mode },
                  {
                    key: "tls_mode",
                    label: "TLS",
                    value: v?.tls_mode,
                    hint: "auto asks a public authority for a certificate; internal uses Caddy's own; an explicit certificate skips ACME entirely and prevents a public one from ever being requested.",
                  },
                  {
                    key: "tls_pending",
                    label: "ACME held",
                    value: v?.tls_pending,
                    goodWhen: false,
                    hint: "Held means this node is deliberately not asking for a public certificate, because the name does not resolve here. Asking anyway would spend a rate limit the whole cluster shares.",
                  },
                  { key: "tenant_id", label: "Belongs to", value: v?.tenant_id ?? "the platform" },
                ]}
              />
            </StateView>
          </Section>

          {v?.mode === "proxy" && !v?.app && (
            <Section title="Backend">
              <BackendEditor domain={domain} current={v?.upstream || ""} />
            </Section>
          )}

          <Section title="Actions">
            <div className="space-y-3">
              <ActionCard
                title="Check this site end to end"
                description="Asks the node to resolve the name, connect to it, and report what it got back — the same journey a visitor makes. This is what distinguishes 'DNS is wrong' from 'the certificate is wrong' from 'the application is down', which look identical from a browser."
                actionLabel="Verify"
                run={() => client.apiFetch(`/caddy/vhosts/${encodeURIComponent(domain)}/verify`, { method: "POST" })}
                invalidates={[["vhost-dns", domain], ["vhost-cert", domain]]}
              />
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="tls">
          <Section title="Certificate" bodyClassName="p-0" actions={<StatusPill tone={certTone}>{certWord}</StatusPill>}>
            <StateView loading={cert.isLoading} error={cert.error} errorContext="the certificate">
              <KeyValue rows={certRows} />
            </StateView>
          </Section>

          {/* POST …/cert/reissue (caddy/certevict.go): a name promoted while Caddy kept its internal
              certificate goes on serving it. This evicts the internal one on THIS node so the public one
              is obtained now. The server refuses a tls-internal site and a name still held here, and the
              card says both before it is pressed. */}
          <Section title="Certificate actions">
            <ActionCard
              title="Replace the internal certificate with a public one"
              description={`Removes this site's internal (Caddy-signed) certificate from storage and from this node's Caddy and reloads the site, so its public certificate is obtained now. On this node only — run it on each node that serves ${domain}.`}
              actionLabel="Reissue"
              danger="medium"
              unavailable={
                v && v.tls_mode !== "auto"
                  ? `This site is set to tls ${v.tls_mode}: its certificate is meant to be what it is, and the server refuses.`
                  : undefined
              }
              preflight={[
                { ok: notTrusted, label: notTrusted ? "The certificate served is not publicly trusted" : "The certificate served is already publicly trusted", detail: notTrusted ? undefined : "A reissue is not needed; it would only obtain the same kind of certificate again." },
                { ok: !v?.tls_pending, label: v?.tls_pending ? "The name is held on this node" : "The name is not held on this node", detail: v?.tls_pending ? "It does not resolve here yet, so the server refuses: a reissue would only produce another internal certificate. Point DNS here and recheck TLS first." : undefined },
              ]}
              run={() => client.apiFetch(`/caddy/vhosts/${encodeURIComponent(domain)}/cert/reissue`, { method: "POST" })}
              invalidates={[["vhost-cert", domain]]}
            />
          </Section>

          <Section
            title="DNS"
            bodyClassName="p-0"
            actions={<StatusPill tone={pointsHere ? "ok" : "warn"}>{pointsHere ? "points here" : "does not point here"}</StatusPill>}
          >
            <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
              {/* Resolving and resolving HERE are different facts, and only the second one makes a
                  site work. On a cluster a name resolving to the other node serves that node's
                  content — with a 200 — which is why the addresses are shown side by side. */}
              A name that resolves is not the same as a name that resolves to this node. If these
              two lists do not overlap, visitors are reaching somewhere else.
            </div>
            <StateView loading={dns.isLoading} error={dns.error} errorContext="DNS for this site">
              <KeyValue
                rows={[
                  { key: "resolvable", label: "Resolves at all", value: d?.resolvable, goodWhen: true },
                  { key: "resolved_ips", label: "Resolves to", value: d?.resolved_ips ?? [] },
                  { key: "node_ips", label: "This node is", value: d?.node_ips ?? [] },
                  { key: "record_types", label: "Record types", value: d?.record_types ?? [] },
                  {
                    key: "pdns_checked",
                    label: "Zone is ours",
                    value: d?.pdns_checked,
                    hint: "When the zone is hosted elsewhere, nothing here can correct a wrong record — it has to be changed wherever the zone lives.",
                  },
                ]}
              />
            </StateView>
          </Section>
        </TabsContent>

        <TabsContent value="configure">
          <Section title="Site settings">
            <StateView loading={vhost.isLoading} error={vhost.error} errorContext="this site's settings">
              <VhostOptionsForm
                opts={vhost.data?.opts ?? {}}
                pluginAvailable={pluginAvail}
                saving={saveOpts.isPending}
                isProxy={["proxy", "app"].includes(vhost.data?.mode ?? "proxy")}
                onSave={(o) => saveOpts.mutate(o)}
              />
            </StateView>
          </Section>
        </TabsContent>

        <TabsContent value="mtls">
          {/* The whole client-certificate panel is shared kit — the account console renders the
              identical thing at Website → Configure. */}
          <MtlsPanel domain={domain} />
        </TabsContent>

        <TabsContent value="log">
          {/* Shared kit — the account console renders the identical review at Websites → Logs. */}
          <SiteLogViewer domain={domain} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
